import {
  createContext, useCallback, useContext, useEffect, useMemo,
  useReducer, useRef,
  type ReactNode,
} from 'react'
import {
  AdminApiClient, AdminApiUnauthorized,
} from '../api/AdminApiClient'
import {
  clearSession as clearStoredSession,
  loadSession as loadStoredSession,
  saveSession as saveStoredSession,
} from './OperatorSession'
import type {
  LoginResponseData, MeResponseData, Operator, OperatorRole,
  SuccessEnvelope,
} from '../api/types'

// AuthProvider drives the SPA's auth state machine and owns the
// AdminApiClient session. RequireRole / TopBar / pages all subscribe
// via useAuth(); they never reach into sessionStorage directly.

type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | {
      status: 'authenticated'
      operator: Operator
      sessionId: string
      expiresAt: number
    }

type AuthAction =
  | { type: 'LOADING' }
  | { type: 'AUTHENTICATED'; operator: Operator; sessionId: string; expiresAt: number }
  | { type: 'UNAUTHENTICATED' }

function authReducer(_: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOADING':
      return { status: 'loading' }
    case 'AUTHENTICATED':
      return {
        status: 'authenticated',
        operator: action.operator,
        sessionId: action.sessionId,
        expiresAt: action.expiresAt,
      }
    case 'UNAUTHENTICATED':
      return { status: 'unauthenticated' }
  }
}

const ROLE_RANK: Record<OperatorRole, number> = {
  VIEWER: 0,
  OPERATOR: 1,
  ADMIN: 2,
}

interface AuthMethods {
  signIn: (googleCredential: string) => Promise<void>
  signOut: () => Promise<void>
  requireRole: (role: OperatorRole) => boolean
}

// Discriminated-union view exposed to consumers: `status` lets
// callers narrow to the authenticated branch without null-checking
// every field individually. Each status is its own arm so TypeScript
// narrows correctly through chained `if (auth.status === '...')`
// checks.
export type ExposedAuth =
  | ({ status: 'loading'; operator: null; sessionId: null } & AuthMethods)
  | ({ status: 'unauthenticated'; operator: null; sessionId: null } & AuthMethods)
  | ({ status: 'authenticated'; operator: Operator; sessionId: string } & AuthMethods)

// Exported so tests can wrap children in a synthetic provider with a
// pre-baked operator/role. Production code should never construct an
// `AuthContext.Provider` itself — use `<AuthProvider>` instead.
export const AuthContext = createContext<ExposedAuth | null>(null)

export function useAuth(): ExposedAuth {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>')
  }
  return ctx
}

interface AuthProviderProps {
  client: AdminApiClient
  children: ReactNode
}

export function AuthProvider({ client, children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, { status: 'loading' })

  // Mirror the latest state into a ref so callbacks registered once
  // (the AdminApiClient onUnauthorized hook in particular) can read the
  // up-to-date status without stale-closure bugs.
  const stateRef = useRef<AuthState>(state)
  stateRef.current = state

  const handleSessionGone = useCallback(() => {
    clearStoredSession()
    client.clearSession()
    dispatch({ type: 'UNAUTHENTICATED' })
  }, [client])

  const signIn = useCallback(
    async (googleCredential: string) => {
      // Sign-in flows are not authenticated, so we don't want a stale
      // X-Operator-Session header attached to /r/admin/login.
      client.clearSession()
      const envelope = await client.post<SuccessEnvelope<LoginResponseData>>(
        '/r/admin/login', { credential: googleCredential },
      )
      const data = envelope.data
      saveStoredSession({ id: data.session_id, expiresAt: data.expires_time })
      client.setSession(data.session_id)
      dispatch({
        type: 'AUTHENTICATED',
        operator: data.operator,
        sessionId: data.session_id,
        expiresAt: data.expires_time,
      })
    },
    [client],
  )

  const signOut = useCallback(async () => {
    try {
      await client.post('/r/admin/logout')
    } catch {
      // Best-effort: a network failure on logout still invalidates the
      // local session. The backend will GC it once it expires.
    }
    handleSessionGone()
  }, [client, handleSessionGone])

  // Bootstrap on mount: rehydrate from sessionStorage and verify with /me.
  useEffect(() => {
    let cancelled = false

    client.onUnauthorized(() => {
      // Only react if we still think we're signed in; the LOGIN call
      // itself returns 401 on bad creds and that path is handled by
      // signIn() throwing.
      if (stateRef.current.status === 'authenticated') {
        handleSessionGone()
      }
    })

    const stored = loadStoredSession()
    if (!stored) {
      dispatch({ type: 'UNAUTHENTICATED' })
      return
    }
    client.setSession(stored.id)
    client.get<SuccessEnvelope<MeResponseData>>('/r/admin/me')
      .then((envelope) => {
        if (cancelled) return
        const data = envelope.data
        saveStoredSession({
          id: stored.id, expiresAt: data.expires_time,
        })
        dispatch({
          type: 'AUTHENTICATED',
          operator: data.operator,
          sessionId: stored.id,
          expiresAt: data.expires_time,
        })
      })
      .catch((e) => {
        if (cancelled) return
        if (e instanceof AdminApiUnauthorized) {
          handleSessionGone()
        } else {
          // Any non-401 means the session might still be valid; we
          // refuse to sign the user out on a transient backend error.
          // Surface as unauthenticated only if there's nothing else we
          // can do — but here the simplest correct behaviour is to
          // treat the session as not-yet-known and bail to the login
          // screen so the operator can retry.
          handleSessionGone()
        }
      })
    return () => {
      cancelled = true
      client.onUnauthorized(null)
    }
    // We deliberately exclude `state.status` from the deps: the callback
    // re-registration would otherwise loop. The signOutRef pattern lets
    // us read the latest state at call time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, handleSessionGone])

  const requireRole = useCallback(
    (role: OperatorRole) => {
      if (state.status !== 'authenticated') return false
      return ROLE_RANK[state.operator.role] >= ROLE_RANK[role]
    },
    [state],
  )

  const value = useMemo<ExposedAuth>(() => {
    if (state.status === 'authenticated') {
      return {
        status: 'authenticated',
        operator: state.operator,
        sessionId: state.sessionId,
        signIn,
        signOut,
        requireRole,
      }
    }
    return {
      status: state.status,
      operator: null,
      sessionId: null,
      signIn,
      signOut,
      requireRole,
    }
  }, [state, signIn, signOut, requireRole])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
